import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  Typography,
  Chip,
  Alert,
  Switch,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import { Add, Edit, Delete, Search } from '@mui/icons-material';
import { PostoCombustivel } from '../types';
import { postoService } from '../services/postoService';
import { useAuth } from '../contexts/AuthContext';
import { getErrorMessage } from '../utils/errors';
import ConfirmDialog from '../components/ConfirmDialog';

const Postos: React.FC = () => {
  const [postos, setPostos] = useState<PostoCombustivel[]>([]);
  const [open, setOpen] = useState(false);
  const [editingPosto, setEditingPosto] = useState<PostoCombustivel | null>(null);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; id: number | null }>({ open: false, id: null });
  const [filtro, setFiltro] = useState('');
  const [formData, setFormData] = useState<PostoCombustivel>({
    nome: '',
    endereco: '',
    telefone: '',
    cnpj: '',
    responsavel: '',
    status: 'ATIVO',
    credenciado: true,
  });

  const { hasRole } = useAuth();

  useEffect(() => {
    loadPostos();
  }, []);

  const loadPostos = async () => {
    try {
      const data = await postoService.listar();
      setPostos(data);
    } catch (error) {
      setErro(getErrorMessage(error));
    }
  };

  const handleOpen = (posto?: PostoCombustivel) => {
    if (posto) {
      setEditingPosto(posto);
      setFormData({ ...posto });
    } else {
      setEditingPosto(null);
      setFormData({ nome: '', endereco: '', telefone: '', cnpj: '', responsavel: '', status: 'ATIVO', credenciado: true });
    }
    setOpen(true);
    setErro('');
  };

  const handleClose = () => {
    setOpen(false);
    setEditingPosto(null);
    setErro('');
  };

  const handleSave = async () => {
    setSalvando(true);
    try {
      if (editingPosto?.id) {
        await postoService.atualizar(editingPosto.id, formData);
      } else {
        await postoService.cadastrar(formData);
      }
      loadPostos();
      handleClose();
    } catch (error) {
      setErro(getErrorMessage(error));
    } finally {
      setSalvando(false);
    }
  };

  const handleDelete = (id: number) => {
    setConfirmDelete({ open: true, id });
  };

  const confirmDeleteAction = async () => {
    if (!confirmDelete.id) return;
    try {
      await postoService.deletar(confirmDelete.id);
      loadPostos();
      setConfirmDelete({ open: false, id: null });
    } catch (error) {
      setErro(getErrorMessage(error));
      setConfirmDelete({ open: false, id: null });
    }
  };

  const formatCnpj = (value: string) => {
    const nums = value.replace(/\D/g, '').slice(0, 14);
    return nums
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  };

  const termo = filtro.trim().toLowerCase();
  const postosFiltrados = termo
    ? postos.filter((item) => Object.values(item).filter(Boolean).some((campo) => String(campo).toLowerCase().includes(termo)))
    : postos;

  return (
    <Container disableGutters maxWidth={false}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4">Postos de Combustível</Typography>
        <Box display="flex" gap={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Pesquisar"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            sx={{ minWidth: 260 }}
          />
          {(hasRole('ADMIN') || hasRole('GERENTE')) && (
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
              Novo Posto
            </Button>
          )}
        </Box>
      </Box>

      {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Endereço</TableCell>
              <TableCell>Telefone</TableCell>
              <TableCell>CNPJ</TableCell>
              <TableCell>Responsável</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Credenciado</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {postosFiltrados.map((posto) => (
              <TableRow key={posto.id}>
                <TableCell>{posto.nome}</TableCell>
                <TableCell>{posto.endereco}</TableCell>
                <TableCell>{posto.telefone}</TableCell>
                <TableCell>{formatCnpj(posto.cnpj || '')}</TableCell>
                <TableCell>{(posto as any).responsavel || posto.contato || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={posto.status === 'ATIVO' ? 'Ativo' : 'Inativo'}
                    color={posto.status === 'ATIVO' ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={posto.credenciado ? 'Sim' : 'Não'}
                    color={posto.credenciado ? 'success' : 'warning'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {(hasRole('ADMIN') || hasRole('GERENTE')) && (
                    <>
                      <IconButton size="small" onClick={() => handleOpen(posto)}>
                        <Edit />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(posto.id!)}>
                        <Delete />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPosto ? 'Editar Posto' : 'Novo Posto'}</DialogTitle>
        <DialogContent>
          {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
          <TextField
            fullWidth
            label="Nome"
            value={formData.nome}
            onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Endereço"
            value={formData.endereco}
            onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Telefone"
            value={formData.telefone}
            onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="CNPJ"
            value={formatCnpj(formData.cnpj || '')}
            onChange={(e) => setFormData({ ...formData, cnpj: e.target.value.replace(/\D/g, '') })}
            margin="normal"
            required
            inputProps={{ maxLength: 18 }}
            placeholder="00.000.000/0000-00"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            label="Responsável"
            value={(formData as any).responsavel || formData.contato || ''}
            onChange={(e) => setFormData({ ...formData, responsavel: e.target.value, contato: e.target.value } as any)}
            margin="normal"
            required
          />
          <Box display="flex" gap={2} sx={{ mt: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
            <TextField
              select
              fullWidth
              label="Status"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as 'ATIVO' | 'INATIVO' })}
              margin="normal"
            >
              <MenuItem value="ATIVO">Ativo</MenuItem>
              <MenuItem value="INATIVO">Inativo</MenuItem>
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.credenciado ?? true}
                  onChange={(e) => setFormData({ ...formData, credenciado: e.target.checked })}
                />
              }
              label="Credenciado pela Prefeitura"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" disabled={salvando}>
            {salvando ? 'Salvando...' : (editingPosto ? 'Atualizar' : 'Cadastrar')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmDelete.open}
        title="Confirmar exclusão"
        message="Tem certeza que deseja remover este posto? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        severity="error"
        onConfirm={confirmDeleteAction}
        onCancel={() => setConfirmDelete({ open: false, id: null })}
      />
    </Container>
  );
};

export default Postos;
